import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
    Grid, GridColumn, Container, Header, Button,
    FormInput, Form, Message
} from 'semantic-ui-react';
import { useQuery } from '@tanstack/react-query';
import { getConfiguration } from './lib/configuration';
import { register, login } from './lib/authentication';
import { useAuthStore } from './lib/authStore';
import { buildPath } from './lib/tenant';
import { t } from './locales';

function Register() {
    const navigate = useNavigate();
    const location = useLocation();

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [errors, setErrors] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const next = new URLSearchParams(location.search).get('next') || buildPath('/events');
    const instanceId = new URLSearchParams(location.search).get('instanceId');
    const qty = new URLSearchParams(location.search).get('qty') || 1;

    const configQuery = useQuery({
        queryKey: ['configuration'],
        queryFn: getConfiguration,
        staleTime: 60 * 60 * 1000,
    });

    const handleSubmit = async () => {
        setErrors([]);

        if (password !== passwordConfirm) {
            setErrors([t('register.passwordMismatch')]);
            return;
        }
        if (password.length < 8) {
            setErrors([t('register.passwordTooShort')]);
            return;
        }

        setSubmitting(true);

        const username = email;
        const result = await register({ username, password, passwordConfirm, firstName, lastName, email });

        if (result === true) {
            const loginResult = await login({ username, password });
            if (loginResult) {
                useAuthStore.getState().login(loginResult.token);
                const checkoutNext = instanceId
                    ? buildPath(`/checkout?instanceId=${instanceId}&qty=${qty}`)
                    : next;
                navigate(checkoutNext, { replace: true });
            }
        } else if (Array.isArray(result)) {
            setErrors(result);
        } else {
            setErrors([t('register.registrationFailed')]);
        }

        setSubmitting(false);
    };

    const logo = configQuery.data?.logo;

    return (
        <Grid stackable columns style={{ padding: '8%' }}>
            <GridColumn color="white" width={3} />
            <GridColumn color="white" width={10}>
                <div className="authCard">
                    {logo && (
                        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                            <img src={logo} alt="Logo" style={{ maxHeight: 60, objectFit: 'contain' }} />
                        </div>
                    )}
                    <Container textAlign="center">
                        <Header className="authHeader">{t('register.title')}</Header>
                        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                            {t('register.subtitle')}
                        </p>

                        {errors.length > 0 && (
                            <Message negative style={{ textAlign: 'left' }}>
                                <ul style={{ margin: 0, paddingLeft: '1rem' }}>
                                    {errors.map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                            </Message>
                        )}

                        <Form>
                            <Form.Group widths="equal">
                                <FormInput
                                    label={t('register.firstNameLabel')}
                                    value={firstName}
                                    onChange={e => setFirstName(e.target.value)}
                                />
                                <FormInput
                                    label={t('register.lastNameLabel')}
                                    value={lastName}
                                    onChange={e => setLastName(e.target.value)}
                                />
                            </Form.Group>
                            <FormInput
                                label={t('register.emailLabel')}
                                type="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                            />
                            <FormInput
                                label={t('register.passwordLabel')}
                                type="password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                            />
                            <FormInput
                                label={t('register.confirmPasswordLabel')}
                                type="password"
                                value={passwordConfirm}
                                onChange={e => setPasswordConfirm(e.target.value)}
                            />
                            <br />
                            <Button
                                fluid
                                className="buttonx"
                                onClick={handleSubmit}
                                loading={submitting}
                                disabled={submitting}
                            >
                                {t('register.submitButton')}
                            </Button>
                        </Form>

                        <p className="options" style={{ marginTop: '1.5rem' }}>
                            {t('register.haveAccount')}
                            <Link to={buildPath('/login') + location.search}> {t('register.signIn')}</Link>
                        </p>
                    </Container>
                </div>
            </GridColumn>
            <GridColumn color="white" width={3} />
        </Grid>
    );
}

export default Register;
